import type { ISelectItem } from '@/components/ui/select/types'

export interface ILanguages extends ISelectItem {
  external?: string
}

export const DEFAULT_COUNTRY = 'LV'
export const BALTIC_COUNTRY_LIST = ['LV', 'EE', 'LT']

export const FREQUENTLY_USED_PHONE_COUNTRY_CODES = ['DK', 'EE', 'FI', 'DE', 'LV', 'LT', 'NO', 'SE', 'GB']

export const FREQUENTLY_USED_COUNTRIES = ['LV', 'LT', 'EE', 'FI', 'DE', 'NO', 'SE', 'GB', 'DK']

export const INTERNAL_LANGUAGES = ['lv', 'et', 'lt', 'en', 'ru', 'de', 'fi', 'uk'] as const
export const BLOG_LANGUAGES = ['lv', 'et', 'lt', 'en', 'ru', 'de', 'fi'] as const

export const OTHER_COUNTRY_ID = 'ZZ'

export type TInternalLanguage = (typeof INTERNAL_LANGUAGES)[number]

export function isInternalLanguage(value: string): value is TInternalLanguage {
  return (INTERNAL_LANGUAGES as readonly string[]).includes(value)
}

export const COUNTRIES_LIST = [
  ...BALTIC_COUNTRY_LIST,
  'AT',
  'BE',
  'CA',
  'CZ',
  'DK',
  'FI',
  'FR',
  'GB',
  'GE',
  'DE',
  'GR',
  'IL',
  'IT',
  'NL',
  'NO',
  'PL',
  'PT',
  'ES',
  'SE',
  'CH',
  'TR',
  'UA',
  'AE',
  'UK',
  OTHER_COUNTRY_ID
] as const

export type TCountriesList = (typeof COUNTRIES_LIST)[number]

export const OTHER_COUNTRY = (translatedLabel: string): ISelectItem => {
  return {
    id: OTHER_COUNTRY_ID,
    label: translatedLabel,
    image: '/other-flag.svg',
    isLocalImage: true
  }
}

const LOCALE_LV = { id: 'lv', label: 'Latviešu' }
const LOCALE_EN = { id: 'en', label: 'English' }
const LOCALE_RU = { id: 'ru', label: 'Русский' }
const LOCALE_DE = { id: 'de', label: 'Deutsch' }
const LOCALE_LT = { id: 'lt', label: 'Lietuvių' }
const LOCALE_ET = { id: 'et', label: 'Eesti' }
const LOCALE_FI = { id: 'fi', label: 'Suomeksi' }
const LOCALE_DA = { id: 'da', label: 'Dansk' }
const LOCALE_SV = { id: 'sv', label: 'Svenska' }
const LOCALE_FR = { id: 'fr', label: 'Français' }
const LOCALE_NL = { id: 'nl', label: 'Nederlands' }
const LOCALE_IT = { id: 'it', label: 'Italiano' }
const LOCALE_ES = { id: 'es', label: 'Español' }
const LOCALE_NO = { id: 'no', label: 'Norsk' }
const LOCALE_PL = { id: 'pl', label: 'Polski' }
const LOCALE_UK = { id: 'uk', label: 'Українська' }
const LOCALE_CA = { id: 'ca', label: 'English' }

export const NEWSLETTER_LOCALES = [LOCALE_EN, LOCALE_LV, LOCALE_LT, LOCALE_ET, LOCALE_FI, LOCALE_DE, LOCALE_RU]

export const LANGUAGE_LIST: { [key: TCountriesList]: ILanguages[] } = {
  LV: [LOCALE_LV, LOCALE_EN, LOCALE_RU, LOCALE_DE, LOCALE_LT, LOCALE_ET, LOCALE_FI, LOCALE_UK],
  EE: [LOCALE_ET, LOCALE_EN, LOCALE_LV, LOCALE_RU, LOCALE_DE, LOCALE_FI, LOCALE_LT, LOCALE_UK],
  LT: [LOCALE_LT, LOCALE_EN, LOCALE_LV, LOCALE_RU, LOCALE_DE, LOCALE_ET, LOCALE_FI, LOCALE_UK],
  AT: [LOCALE_DE, LOCALE_EN, LOCALE_LV, LOCALE_RU, LOCALE_LT, LOCALE_ET, LOCALE_FI, LOCALE_UK],
  BE: [
    LOCALE_EN,
    LOCALE_LV,
    LOCALE_RU,
    LOCALE_LT,
    LOCALE_ET,
    LOCALE_DE,
    LOCALE_FI,
    { ...LOCALE_FR, external: 'https://www.airbaltic.com/fr-be/offres-de-vols/vols' },
    { ...LOCALE_NL, external: 'https://www.airbaltic.com/nl-be/vlucht-deals/vluchten' },
    LOCALE_UK
  ],
  CZ: [LOCALE_EN, LOCALE_LV, LOCALE_RU, LOCALE_LT, LOCALE_ET, LOCALE_DE, LOCALE_FI, LOCALE_UK],
  DK: [
    LOCALE_EN,
    LOCALE_LV,
    { ...LOCALE_DA, external: 'https://www.airbaltic.com/da-dk/flyaftaler/flyrejser' },
    LOCALE_RU,
    LOCALE_LT,
    LOCALE_ET,
    LOCALE_DE,
    LOCALE_FI,
    LOCALE_UK
  ],
  FI: [
    LOCALE_FI,
    LOCALE_EN,
    LOCALE_LV,
    LOCALE_RU,
    LOCALE_LT,
    LOCALE_ET,
    LOCALE_DE,
    { ...LOCALE_SV, external: 'https://www.airbaltic.com/sv-fi/flygerbjudanden/flyg' },
    LOCALE_UK
  ],
  FR: [
    LOCALE_EN,
    LOCALE_LV,
    { ...LOCALE_FR, external: 'https://www.airbaltic.com/fr-fr/offres-de-vols/vols' },
    LOCALE_RU,
    LOCALE_LT,
    LOCALE_ET,
    LOCALE_DE,
    LOCALE_FI,
    { ...LOCALE_ES, external: 'https://www.airbaltic.com/es-fr/ofertas-de-vuelos/vuelos' },
    LOCALE_UK
  ],
  GB: [LOCALE_LV, LOCALE_EN, LOCALE_RU, LOCALE_DE, LOCALE_LT, LOCALE_ET, LOCALE_FI, LOCALE_UK],
  GE: [LOCALE_EN, LOCALE_LV, LOCALE_RU, LOCALE_LT, LOCALE_ET, LOCALE_FI, LOCALE_DE, LOCALE_UK],
  DE: [LOCALE_DE, LOCALE_EN, LOCALE_LV, LOCALE_RU, LOCALE_LT, LOCALE_ET, LOCALE_FI, LOCALE_UK],
  GR: [LOCALE_EN, LOCALE_LV, LOCALE_RU, LOCALE_LT, LOCALE_ET, LOCALE_FI, LOCALE_DE, LOCALE_UK],
  IL: [LOCALE_EN, LOCALE_LV, LOCALE_RU, LOCALE_LT, LOCALE_ET, LOCALE_FI, LOCALE_DE, LOCALE_UK],
  IT: [
    LOCALE_EN,
    LOCALE_LV,
    { ...LOCALE_IT, external: 'https://www.airbaltic.com/it-it/offerte-di-voli/voli' },
    LOCALE_RU,
    LOCALE_LT,
    LOCALE_ET,
    LOCALE_DE,
    LOCALE_FI,
    { ...LOCALE_ES, external: 'https://www.airbaltic.com/es-it/ofertas-de-vuelos/vuelos' },
    LOCALE_UK
  ],
  NL: [
    LOCALE_EN,
    LOCALE_LV,
    { ...LOCALE_NL, external: 'https://www.airbaltic.com/nl-nl/vlucht-deals/vluchten' },
    LOCALE_RU,
    LOCALE_LT,
    LOCALE_ET,
    LOCALE_FI,
    LOCALE_DE,
    LOCALE_UK
  ],
  NO: [
    LOCALE_EN,
    LOCALE_LV,
    { ...LOCALE_NO, external: 'https://www.airbaltic.com/no-no/flytilbud/flyreiser' },
    LOCALE_RU,
    LOCALE_LT,
    LOCALE_ET,
    LOCALE_DE,
    LOCALE_FI,
    LOCALE_UK
  ],
  PL: [
    LOCALE_EN,
    LOCALE_LV,
    { ...LOCALE_PL, external: 'https://www.airbaltic.com/pl-pl/oferty-lotnicze/loty' },
    LOCALE_RU,
    LOCALE_LT,
    LOCALE_ET,
    LOCALE_DE,
    LOCALE_FI,
    LOCALE_UK
  ],
  PT: [
    LOCALE_EN,
    LOCALE_LV,
    LOCALE_RU,
    LOCALE_LT,
    LOCALE_ET,
    LOCALE_DE,
    LOCALE_FI,
    { ...LOCALE_ES, external: 'https://www.airbaltic.com/es-pt/ofertas-de-vuelos/vuelos' },
    LOCALE_UK
  ],
  ES: [
    LOCALE_EN,
    LOCALE_LV,
    { ...LOCALE_ES, external: 'https://www.airbaltic.com/es-es/ofertas-de-vuelos/vuelos' },
    LOCALE_RU,
    LOCALE_LT,
    LOCALE_ET,
    LOCALE_DE,
    LOCALE_FI,
    LOCALE_UK
  ],
  SE: [
    LOCALE_EN,
    LOCALE_LV,
    { ...LOCALE_SV, external: 'https://www.airbaltic.com/sv-se/flygerbjudanden/flyg' },
    LOCALE_RU,
    LOCALE_LT,
    LOCALE_ET,
    LOCALE_DE,
    LOCALE_FI,
    LOCALE_UK
  ],
  CH: [
    LOCALE_EN,
    LOCALE_LV,
    LOCALE_DE,
    LOCALE_RU,
    LOCALE_LT,
    LOCALE_ET,
    LOCALE_FI,
    { ...LOCALE_FR, external: 'https://www.airbaltic.com/fr-ch/offres-de-vols/vols' },
    { ...LOCALE_IT, external: 'https://www.airbaltic.com/it-ch/offerte-di-voli/voli' },
    LOCALE_UK
  ],
  TR: [LOCALE_EN, LOCALE_LV, LOCALE_RU, LOCALE_LT, LOCALE_ET, LOCALE_DE, LOCALE_FI, LOCALE_UK],
  UA: [LOCALE_UK, LOCALE_EN, LOCALE_LV, LOCALE_RU, LOCALE_LT, LOCALE_ET, LOCALE_DE, LOCALE_FI],
  AE: [LOCALE_EN, LOCALE_LV, LOCALE_RU, LOCALE_LT, LOCALE_ET, LOCALE_DE, LOCALE_FI, LOCALE_UK],
  UK: [LOCALE_EN, LOCALE_LV, LOCALE_RU, LOCALE_LT, LOCALE_ET, LOCALE_DE, LOCALE_FI, LOCALE_UK],
  ZZ: [
    LOCALE_EN,
    LOCALE_LV,
    LOCALE_RU,
    LOCALE_LT,
    LOCALE_ET,
    LOCALE_DE,
    LOCALE_FI,
    LOCALE_UK,
    { ...LOCALE_NO, external: 'https://www.airbaltic.com/no/flytilbud/flyreiser' },
    { ...LOCALE_NL, external: 'https://www.airbaltic.com/nl-nl/vlucht-deals/vluchten' },
    { ...LOCALE_SV, external: 'https://www.airbaltic.com/sv/flygerbjudanden/flyg' },
    { ...LOCALE_ES, external: 'https://www.airbaltic.com/es/ofertas-de-vuelos/vuelos' },
    { ...LOCALE_FR, external: 'https://www.airbaltic.com/fr/offres-de-vols/vols' },
    { ...LOCALE_IT, external: 'https://www.airbaltic.com/it/offerte-di-voli/voli' },
    { ...LOCALE_DA, external: 'https://www.airbaltic.com/da/flyaftaler/flyrejser' },
    { ...LOCALE_PL, external: 'https://www.airbaltic.com/pl/oferty-lotnicze/loty' }
  ],
  CA: [LOCALE_EN, LOCALE_LV, LOCALE_RU, LOCALE_LT, LOCALE_ET, LOCALE_DE, LOCALE_FI, LOCALE_UK]
}
